import { create } from 'zustand';
import { useMyPrefsStore } from '@/lib/myPrefs';
import type { MirrorPrefs } from '@/lib/myPrefs';

/**
 * Floating mirror window state.
 *
 * 历史 / 数据模型：
 *
 *   - v1：`mode: 'docked' | 'floating'` + Dashboard 发布的 `dockRect`，镜像
 *     可以 snap 进 overview tab。耦合太紧已废弃。
 *   - v2：始终是浮动窗口；从侧边栏 per-session block 打开 / 隐藏。`minimized`
 *     现在表示「连接中但藏起来」——底层 scrcpy WS / WebCodecs 解码器还活着。
 *
 * 阶段 A 持久化：所有可持久字段都收纳进 B1 类 `prefs.global.mirror`，跟账号走、
 * 跨电脑同步。`isOpen / serial / minimized / isFullscreen / readOnly` 是会话级
 * transient 状态，永远不持久（避免静默重连消耗资源）。
 */

export interface MirrorPos {
  x: number;
  y: number;
}

export interface MirrorSize {
  w: number;
  h: number;
}

/**
 * scrcpy 画质参数。
 *
 *   - `maxSize: -1` → 自动（让 server 按 transport 选档）
 *   - `maxSize: 0`  → 原生（不缩放）
 *   - `maxFps: -1`  → 同上
 */
export interface MirrorQuality {
  maxSize: number;
  maxFps: number;
}

export type LockOrientation = -1 | 0 | 1 | 2 | 3;

const DEFAULT_SIZE: MirrorSize = { w: 360, h: 640 };
export const SIDE_TOOLS_MIN_W = 44;
export const SIDE_TOOLS_MAX_W = 220;
export const SIDE_TOOLS_DEFAULT_W = 48;
export const SIDE_TOOLS_LABEL_THRESHOLD = 96;

const DEFAULT_QUALITY: MirrorQuality = { maxSize: -1, maxFps: -1 };

/** Bitrate 滑杆档位 (kbps)。粗略对齐 QtScrcpy。 */
export const BITRATE_PRESETS_KBPS: Array<number | null> = [
  null,
  512,
  1024,
  2048,
  4096,
  8192,
  16384,
];

function defaultPos(): MirrorPos {
  if (typeof window === 'undefined') return { x: 24, y: 24 };
  const x = Math.max(24, window.innerWidth - DEFAULT_SIZE.w - 24);
  const y = Math.max(24, window.innerHeight - DEFAULT_SIZE.h - 24);
  return { x, y };
}

const MIN_W = 220;
const MAX_W = 900;
const MIN_H = 320;
const MAX_H = 1400;

export function sanitizeSize(s: MirrorSize): MirrorSize {
  return {
    w: Math.max(MIN_W, Math.min(MAX_W, Math.round(s.w))),
    h: Math.max(MIN_H, Math.min(MAX_H, Math.round(s.h))),
  };
}

function sanitizeQuality(q: MirrorQuality): MirrorQuality {
  const allowedSizes = new Set([-1, 0, 540, 720, 1080]);
  return {
    maxSize: allowedSizes.has(q.maxSize) ? q.maxSize : -1,
    maxFps: q.maxFps === -1 || (q.maxFps > 0 && q.maxFps <= 120) ? q.maxFps : -1,
  };
}

function sanitizeBitRate(v: unknown): number | null {
  if (v == null) return null;
  if (typeof v !== 'number' || !Number.isFinite(v)) return null;
  const presets = BITRATE_PRESETS_KBPS.filter((p): p is number => p != null);
  const closest = presets.reduce((a, b) => (Math.abs(b - v) < Math.abs(a - v) ? b : a));
  return closest;
}

function sanitizeLockOrientation(v: unknown): LockOrientation {
  if (v === 0 || v === 1 || v === 2 || v === 3 || v === -1) return v;
  return -1;
}

function sanitizeSideToolsWidth(v: unknown): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return SIDE_TOOLS_DEFAULT_W;
  return Math.round(Math.max(SIDE_TOOLS_MIN_W, Math.min(SIDE_TOOLS_MAX_W, v)));
}

function sanitizeSideToolsCollapsed(v: unknown): Record<string, boolean> {
  if (!v || typeof v !== 'object') return {};
  const out: Record<string, boolean> = {};
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (typeof val === 'boolean') out[k] = val;
  }
  return out;
}

/**
 * Clamp a position so at least 64px of the title bar stays inside the
 * viewport. Uses the *current* size to derive a left-bound (so when the
 * window shrinks, we leave room for resize handles too).
 */
export function clampPosToViewport(pos: MirrorPos, size: MirrorSize): MirrorPos {
  if (typeof window === 'undefined') return pos;
  const minX = -(size.w - 64);
  const maxX = window.innerWidth - 64;
  const minY = 0;
  const maxY = Math.max(0, window.innerHeight - 32);
  return {
    x: Math.max(minX, Math.min(maxX, pos.x)),
    y: Math.max(minY, Math.min(maxY, pos.y)),
  };
}

/**
 * Persisted shape derived from B1 prefs.global.mirror. The store presents the
 * historical (`pos / size / quality / videoBitRateKbps / ...`) shape to
 * downstream components so they don't need to know the prefs schema.
 */
function shapeFromPrefs(p: MirrorPrefs): {
  pos: MirrorPos;
  size: MirrorSize;
  quality: MirrorQuality;
  videoBitRateKbps: number | null;
  showLogcat: boolean;
  showServerLog: boolean;
  hudVisible: boolean;
  stayAwake: boolean;
  lockOrientation: LockOrientation;
  pinned: boolean;
  sideToolsWidth: number;
  sideToolsCollapsed: Record<string, boolean>;
} {
  const layout = p.layout;
  const pos = layout ? clampPosToViewport({ x: layout.x, y: layout.y }, { w: layout.width, h: layout.height }) : defaultPos();
  const size = layout ? sanitizeSize({ w: layout.width, h: layout.height }) : DEFAULT_SIZE;
  const quality = sanitizeQuality({
    maxSize: p.maxSize ?? DEFAULT_QUALITY.maxSize,
    maxFps: p.maxFps ?? DEFAULT_QUALITY.maxFps,
  });
  // server-side bitRate stored in bps；本地 UI 用 kbps。
  const videoBitRateKbps =
    p.bitRate == null ? null : sanitizeBitRate(Math.round(p.bitRate / 1000));
  return {
    pos,
    size,
    quality,
    videoBitRateKbps,
    showLogcat: !!p.showLogcat,
    showServerLog: !!p.showServerLog,
    hudVisible: p.hudVisible !== false,
    stayAwake: !!p.stayAwake,
    lockOrientation: sanitizeLockOrientation(p.lockOrientation),
    pinned: !!p.pinned,
    sideToolsWidth: sanitizeSideToolsWidth(p.sideToolsWidth),
    sideToolsCollapsed: sanitizeSideToolsCollapsed(p.sideToolsCollapsed),
  };
}

interface MirrorState {
  pos: MirrorPos;
  size: MirrorSize;
  quality: MirrorQuality;
  videoBitRateKbps: number | null;
  showLogcat: boolean;
  showServerLog: boolean;
  hudVisible: boolean;
  stayAwake: boolean;
  lockOrientation: LockOrientation;
  pinned: boolean;
  sideToolsWidth: number;
  sideToolsCollapsed: Record<string, boolean>;

  isOpen: boolean;
  serial: string | null;
  minimized: boolean;
  isFullscreen: boolean;
  readOnly: boolean;

  open: (serial: string) => void;
  close: () => void;
  setMinimized: (v: boolean) => void;
  toggleMinimized: () => void;
  setFullscreen: (v: boolean) => void;
  toggleFullscreen: () => void;
  setPos: (p: MirrorPos) => void;
  setSize: (s: MirrorSize) => void;
  setQuality: (q: MirrorQuality) => void;
  toggleLogcat: () => void;
  toggleServerLog: () => void;
  toggleHud: () => void;
  setVideoBitRate: (kbps: number | null) => void;
  setStayAwake: (v: boolean) => void;
  setLockOrientation: (v: LockOrientation) => void;
  togglePinned: () => void;
  setReadOnly: (v: boolean) => void;
  setSideToolsWidth: (w: number) => void;
  setSideToolsGroupCollapsed: (groupId: string, collapsed: boolean) => void;
  toggleSideToolsGroup: (groupId: string) => void;
}

function pushPrefs(patch: Partial<MirrorPrefs>): void {
  useMyPrefsStore.getState().updateGlobal({ mirror: patch });
}

function persistLayoutFromState(s: MirrorState): void {
  pushPrefs({
    layout: { width: s.size.w, height: s.size.h, x: s.pos.x, y: s.pos.y },
  });
}

const initial = shapeFromPrefs(useMyPrefsStore.getState().global.mirror);

export const useMirror = create<MirrorState>((set, get) => ({
  ...initial,

  isOpen: false,
  serial: null,
  minimized: false,
  isFullscreen: false,
  readOnly: false,

  open: (serial) =>
    set(() => ({
      isOpen: true,
      serial,
      minimized: false,
    })),

  close: () =>
    set(() => ({
      isOpen: false,
      serial: null,
      minimized: false,
      isFullscreen: false,
    })),

  setMinimized: (v) => {
    if (get().minimized === v) return;
    set({ minimized: v });
  },

  toggleMinimized: () => {
    set({ minimized: !get().minimized });
  },

  setFullscreen: (v) => {
    if (get().isFullscreen === v) return;
    if (v) set({ isFullscreen: true, minimized: false });
    else set({ isFullscreen: false });
  },

  toggleFullscreen: () => {
    const next = !get().isFullscreen;
    if (next) set({ isFullscreen: true, minimized: false });
    else set({ isFullscreen: false });
  },

  setPos: (p) => {
    const clamped = clampPosToViewport(p, get().size);
    set({ pos: clamped });
    persistLayoutFromState(get());
  },

  setSize: (s) => {
    const sanitized = sanitizeSize(s);
    const pos = clampPosToViewport(get().pos, sanitized);
    set({ size: sanitized, pos });
    persistLayoutFromState(get());
  },

  setQuality: (q) => {
    const sanitized = sanitizeQuality(q);
    set({ quality: sanitized });
    pushPrefs({ maxSize: sanitized.maxSize, maxFps: sanitized.maxFps });
  },

  toggleLogcat: () => {
    const next = !get().showLogcat;
    set({ showLogcat: next });
    pushPrefs({ showLogcat: next });
  },

  toggleServerLog: () => {
    const next = !get().showServerLog;
    set({ showServerLog: next });
    pushPrefs({ showServerLog: next });
  },

  toggleHud: () => {
    const next = !get().hudVisible;
    set({ hudVisible: next });
    pushPrefs({ hudVisible: next });
  },

  setVideoBitRate: (kbps) => {
    if (get().videoBitRateKbps === kbps) return;
    set({ videoBitRateKbps: kbps });
    pushPrefs({ bitRate: kbps == null ? null : kbps * 1000 });
  },

  setStayAwake: (v) => {
    if (get().stayAwake === v) return;
    set({ stayAwake: v });
    pushPrefs({ stayAwake: v });
  },

  setLockOrientation: (v) => {
    if (get().lockOrientation === v) return;
    set({ lockOrientation: v });
    pushPrefs({ lockOrientation: v });
  },

  togglePinned: () => {
    const next = !get().pinned;
    set({ pinned: next });
    pushPrefs({ pinned: next });
  },

  setReadOnly: (v) => {
    if (get().readOnly === v) return;
    set({ readOnly: v });
  },

  setSideToolsWidth: (w) => {
    const clamped = sanitizeSideToolsWidth(w);
    if (get().sideToolsWidth === clamped) return;
    set({ sideToolsWidth: clamped });
    pushPrefs({ sideToolsWidth: clamped });
  },

  setSideToolsGroupCollapsed: (groupId, collapsed) => {
    const cur = get().sideToolsCollapsed;
    const wasCollapsed = !!cur[groupId];
    if (wasCollapsed === collapsed) return;
    const next: Record<string, boolean> = { ...cur };
    if (collapsed) next[groupId] = true;
    else delete next[groupId];
    set({ sideToolsCollapsed: next });
    pushPrefs({ sideToolsCollapsed: next });
  },

  toggleSideToolsGroup: (groupId) => {
    const cur = get().sideToolsCollapsed;
    const wasCollapsed = !!cur[groupId];
    const next: Record<string, boolean> = { ...cur };
    if (wasCollapsed) delete next[groupId];
    else next[groupId] = true;
    set({ sideToolsCollapsed: next });
    pushPrefs({ sideToolsCollapsed: next });
  },
}));

// Hydrate once prefs become ready: prefs returned the server-backed truth,
// stamp it into the store so the floating window reopens at the saved
// layout / quality on next session.
if (typeof window !== 'undefined') {
  useMyPrefsStore.subscribe((state, prev) => {
    if (state.ready && !prev.ready) {
      const next = shapeFromPrefs(state.global.mirror);
      useMirror.setState(next);
    }
  });
  // Re-clamp on viewport resize so a smaller browser window doesn't
  // strand the floating mirror off-screen.
  window.addEventListener('resize', () => {
    const s = useMirror.getState();
    const clamped = clampPosToViewport(s.pos, s.size);
    if (clamped.x !== s.pos.x || clamped.y !== s.pos.y) {
      useMirror.setState({ pos: clamped });
      persistLayoutFromState(useMirror.getState());
    }
  });
}
